import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAnalyticsTracker from '../../../../../../../analytics/hooks/useAnalyticsTracker';
import { WorkspaceTrackingActions } from '../../../../tracking';
import { Button, IconButton, Modal, TextField, toasterService } from '@hevoio/hevo-ui-toolkit';
import { WorkspacesAPI } from '../../../../api/WorkspacesAPI';
import { getErrorMessageFromObj } from '../../../../../../../core/utils/request';
import { validateClientName } from '../../../../constants/workspaceValidation';
import styles from './CreateWorkspaceModal.module.scss';
import { IconFileDots, IconX } from '@tabler/icons-react';
import useService from '../../../../../../../injector/hooks/useService';
import { UserService } from '../../../../../../../core/services/user.service';

interface CreateWorkspaceModalProps {
  open: boolean;
  onClose: () => void;
}

export function CreateWorkspaceModal({ open, onClose }: CreateWorkspaceModalProps) {
  const navigate = useNavigate();
  const userService = useService(UserService);
  const { eventTrack } = useAnalyticsTracker();
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [dirty, setDirty] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const validate = validateClientName;

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setName(value);
    setDirty(true);
    if (error) {
      setError(validate(value));
    }
  };

  const handleClose = () => {
    setName('');
    setError('');
    setDirty(false);
    setSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    const validationError = validate(name);
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    try {
      const res = await WorkspacesAPI.createWorkspace({ name: name.trim() });
      toasterService.pop(
        'success',
        res.success_message || `Workspace "${res.data.name}" Created Successfully!`,
        "You can now add users and create Pipelines"
        
      );
      eventTrack({
        action: WorkspaceTrackingActions.CREATE_WORKSPACE_SUCCESS,
        properties: { workspaceId: res.data.workspace_id, workspaceName: res.data.name }
      });
      userService.addWorkspaceToMemberships(res.data);
      handleClose();
      navigate(`/team/workspaces/${res.data.workspace_id}`);
    } catch (err) {
      setError(getErrorMessageFromObj(err) || 'Failed to create workspace. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose} contentClassName={styles.modalContent}>
      <div className={styles.modal}>
        <div className='dialog-header'>
          <div className={styles.headerContent}>
            <div className={styles.headerIcon}>
              <IconFileDots size={24} />
            </div>
            <span className={styles.headerTitle}>Create Workspace</span>
          </div>
          <IconButton onClick={handleClose} dataId='create-workspace-modal-close' label='Close'>
            <IconX size={20} />
          </IconButton>
        </div>

        <div className='dialog-body'>
          <p className={styles.description}>
            A new Workspace will be created for this client. You can configure Pipelines and Destinations after setup.
          </p>

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel} htmlFor='workspace-client-name'>
              Client Name<span className={styles.required}>*</span>
            </label>
            <TextField
              id='workspace-client-name'
              dataId='workspace-client-name'
              placeholder='acme'
              value={name}
              onChange={handleNameChange}
              onBlur={() => dirty && setError(validate(name))}
              onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handleSubmit()}
              error={!!error}
              helperText={error}
              size='small'
              autoFocus
            />
          </div>
        </div>

        <div className='dialog-footer'>
          <Button
            className={styles.actionBtn}
            style={{ width: 70, minWidth: 70 }}
            variation='outline'
            onClick={handleClose}
            disabled={submitting}
            dataId='create-workspace-cancel'
          >
            Cancel
          </Button>
          <Button
            className={`btn-primary ${styles.actionBtn}`}
            style={{ width: 147 }}
            onClick={handleSubmit}
            showProgress={submitting}
            disabled={submitting}
            dataId='create-workspace-submit'
          >
            Create Workspace
          </Button>
        </div>
      </div>
    </Modal>
  );
}
